import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Zap, 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  Rocket,
  Settings,
  PartyPopper,
  Search,
  Pencil,
  Trash2,
  Plus,
  Shield,
  Wind,
  Focus,
  Target
} from 'lucide-react';

// Preset laser configurations
const laserPresets = {
  co2: [
    { brand: 'Generic', model: 'K40', power: 40, bed: '300x200', cooling: 'water', type: 'co2' },
    { brand: 'OMTech', model: '40W CO2', power: 40, bed: '500x300', cooling: 'water', type: 'co2' },
    { brand: 'OMTech', model: '60W CO2', power: 60, bed: '600x400', cooling: 'water', type: 'co2' },
    { brand: 'OMTech', model: '80W CO2', power: 80, bed: '700x500', cooling: 'water', type: 'co2' },
    { brand: 'OMTech', model: '100W CO2', power: 100, bed: '900x600', cooling: 'water', type: 'co2' },
    { brand: 'Thunder Laser', model: 'Nova 35', power: 80, bed: '900x600', cooling: 'water', type: 'co2' },
    { brand: 'Thunder Laser', model: 'Nova 51', power: 100, bed: '1300x900', cooling: 'water', type: 'co2' },
    { brand: 'xTool', model: 'P2 55W', power: 55, bed: '600x450', cooling: 'air', type: 'co2' },
    { brand: 'Full Spectrum', model: 'Muse', power: 45, bed: '508x305', cooling: 'water', type: 'co2' },
    { brand: 'Full Spectrum', model: 'Hobby', power: 40, bed: '508x305', cooling: 'water', type: 'co2' },
    { brand: 'Bodor', model: 'Entry Series', power: 60, bed: '600x400', cooling: 'water', type: 'co2' },
    { brand: 'Bodor', model: 'Professional', power: 130, bed: '1300x900', cooling: 'water', type: 'co2' },
    { brand: 'Epilog', model: 'Zing 16', power: 30, bed: '406x305', cooling: 'air', type: 'co2' },
    { brand: 'Epilog', model: 'Fusion M2', power: 60, bed: '813x508', cooling: 'air', type: 'co2' },
  ],
  diode: [
    { brand: 'xTool', model: 'D1 Pro 10W', power: 10, bed: '432x406', cooling: 'air', type: 'diode' },
    { brand: 'xTool', model: 'D1 Pro 20W', power: 20, bed: '432x406', cooling: 'air', type: 'diode' },
    { brand: 'xTool', model: 'S1 40W', power: 40, bed: '498x319', cooling: 'air', type: 'diode' },
    { brand: 'xTool', model: 'M1 Hybrid', power: 10, bed: '380x300', cooling: 'air', type: 'diode' },
    { brand: 'Sculpfun', model: 'S30 Pro', power: 10, bed: '410x420', cooling: 'air', type: 'diode' },
    { brand: 'Sculpfun', model: 'S30 Pro Max', power: 20, bed: '410x400', cooling: 'air', type: 'diode' },
    { brand: 'Atomstack', model: 'X20 Pro', power: 20, bed: '400x400', cooling: 'air', type: 'diode' },
    { brand: 'Atomstack', model: 'A20 Pro', power: 20, bed: '850x400', cooling: 'air', type: 'diode' },
    { brand: 'Ortur', model: 'Laser Master 3', power: 10, bed: '400x400', cooling: 'air', type: 'diode' },
    { brand: 'Ortur', model: 'LM3 LE', power: 5, bed: '400x400', cooling: 'air', type: 'diode' },
    { brand: 'Comgrow', model: 'Z1 Pro', power: 10, bed: '400x400', cooling: 'air', type: 'diode' },
    { brand: 'NEJE', model: 'Master 2S Max', power: 30, bed: '460x810', cooling: 'air', type: 'diode' },
  ],
  fibre: [
    { brand: 'xTool', model: 'F1', power: 20, bed: '115x115', cooling: 'air', type: 'fibre' },
    { brand: 'Various', model: 'MOPA 30W Fibre', power: 30, bed: '150x150', cooling: 'air', type: 'fibre' },
    { brand: 'Various', model: 'MOPA 50W Fibre', power: 50, bed: '200x200', cooling: 'air', type: 'fibre' },
    { brand: 'Raycus/IPG', model: '20W Fibre Marker', power: 20, bed: '110x110', cooling: 'air', type: 'fibre' },
    { brand: 'Raycus/IPG', model: '50W Fibre Marker', power: 50, bed: '200x200', cooling: 'air', type: 'fibre' },
  ],
  galvo: [
    { brand: 'Various', model: 'Desktop Galvo 20W CO2', power: 20, bed: '200x200', cooling: 'air', type: 'galvo' },
    { brand: 'Various', model: 'Desktop Galvo 30W Fibre', power: 30, bed: '150x150', cooling: 'air', type: 'galvo' },
  ],
};

const LaserSetupWizard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [mode, setMode] = useState('select'); // 'select', 'preset', 'custom', 'manage'
  const [lasers, setLasers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLaser, setEditingLaser] = useState(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [laserType, setLaserType] = useState('co2');
  const [searchTerm, setSearchTerm] = useState('');

  // Custom laser form
  const [laserForm, setLaserForm] = useState({
    name: '',
    brand: '',
    model: '',
    laser_type: 'co2',
    power_watts: 40,
    bed_width: 300,
    bed_height: 200,
    cooling_type: 'water',
    has_air_assist: true,
    has_autofocus: false,
    focal_length: 50.8,
    max_speed: 500,
    controller: 'ruida'
  });

  // Fetch user's lasers
  useEffect(() => {
    const fetchLasers = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/laser/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const laserList = response.data || [];
        setLasers(laserList);
        
        // Restore active laser from localStorage if it exists
        const storedLaserId = localStorage.getItem('active_machine_laser');
        if (storedLaserId && laserList.find(l => l.id === storedLaserId)) {
          // Active laser is already set via the stored ID
        } else if (laserList.length > 0) {
          // Default to first laser
          localStorage.setItem('active_machine_laser', laserList[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch lasers:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLasers();
  }, [token]);

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  // Filter presets by search and type
  const filteredPresets = laserPresets[laserType]?.filter(preset => 
    !searchTerm || 
    preset.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
    preset.model.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Handle preset selection
  const selectPreset = (preset) => {
    setLaserForm({
      name: `${preset.brand} ${preset.model}`,
      brand: preset.brand,
      model: preset.model,
      laser_type: preset.type,
      power_watts: preset.power,
      bed_width: parseInt(preset.bed.split('x')[0]),
      bed_height: parseInt(preset.bed.split('x')[1]),
      cooling_type: preset.cooling,
      has_air_assist: true,
      has_autofocus: preset.brand === 'Glowforge',
      focal_length: 50.8,
      max_speed: preset.type === 'diode' ? 10000 : 500,
      controller: preset.brand === 'Glowforge' ? 'proprietary' : (preset.type === 'diode' ? 'grbl' : 'ruida')
    });
    setMode('custom');
    setStep(3); // Skip to configuration step
  };

  // Save laser
  const saveLaser = async () => {
    if (!laserForm.name) {
      toast.error('Please enter a name for your laser');
      return;
    }

    try {
      let savedLaserId;
      if (editingLaser) {
        await axios.put(`${API}/laser/machines/${editingLaser.id}`, laserForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedLaserId = editingLaser.id;
        toast.success('Laser updated!');
      } else {
        const createResponse = await axios.post(`${API}/laser/machines`, laserForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedLaserId = createResponse.data.id;
        // Set newly created laser as active
        localStorage.setItem('active_machine_laser', savedLaserId);
        toast.success('Laser added!');
      }
      
      // Refresh list
      const response = await axios.get(`${API}/laser/machines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLasers(response.data || []);
      
      setStep(5); // Go to success screen
    } catch (error) {
      console.error('Failed to save laser:', error);
      toast.error('Failed to save laser');
    }
  };

  // Delete laser
  const deleteLaser = async (laserId) => {
    if (!window.confirm('Are you sure you want to delete this laser?')) return;
    
    try {
      await axios.delete(`${API}/laser/machines/${laserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedLasers = lasers.filter(l => l.id !== laserId);
      setLasers(updatedLasers);
      
      // If we deleted the active laser, clear localStorage or set to next available
      const activeLaserId = localStorage.getItem('active_machine_laser');
      if (activeLaserId === laserId) {
        if (updatedLasers.length > 0) {
          localStorage.setItem('active_machine_laser', updatedLasers[0].id);
        } else {
          localStorage.removeItem('active_machine_laser');
        }
      }
      toast.success('Laser deleted');
    } catch (error) {
      toast.error('Failed to delete laser');
    }
  };

  // Edit laser
  const startEditLaser = (laser) => {
    setEditingLaser(laser);
    setLaserForm({
      name: laser.name,
      brand: laser.brand || '',
      model: laser.model || '',
      laser_type: laser.laser_type,
      power_watts: laser.power_watts,
      bed_width: laser.bed_width,
      bed_height: laser.bed_height,
      cooling_type: laser.cooling_type || 'water',
      has_air_assist: laser.has_air_assist ?? true,
      has_autofocus: laser.has_autofocus ?? false,
      focal_length: laser.focal_length || 50.8,
      max_speed: laser.max_speed || 500,
      controller: laser.controller || 'ruida'
    });
    setMode('custom');
    setStep(3);
  };

  // ==========================================
  // MODE: SELECT (Initial Screen)
  // ==========================================
  if (mode === 'select') {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="laser-setup-wizard"
        style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
      >
        <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/20 mb-4">
              <Zap className="w-8 h-8 text-orange-500" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-2">CONFIGURE YOUR LASER</div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Laser Setup
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-2">Configure your CO2 or diode laser for optimal results</p>
          </div>

          {/* Existing Lasers */}
          {lasers.length > 0 && (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Your Lasers</CardTitle>
                <CardDescription className="text-zinc-400">
                  {lasers.length} laser{lasers.length !== 1 ? 's' : ''} configured
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {lasers.map((laser) => (
                  <div 
                    key={laser.id}
                    className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 flex items-center justify-between group hover:border-orange-500/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Zap className={`w-5 h-5 ${laser.laser_type === 'co2' ? 'text-blue-400' : 'text-purple-400'}`} />
                      <div>
                        <div className="text-white font-medium">{laser.name}</div>
                        <div className="text-xs text-zinc-500">
                          {laser.laser_type.toUpperCase()} • {laser.power_watts}W • {laser.bed_width}x{laser.bed_height}mm
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditLaser(laser)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteLaser(laser.id)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card 
              className="bg-black/70 backdrop-blur-sm border-zinc-700/50 hover:border-orange-500 transition-all cursor-pointer"
              onClick={() => setMode('preset')}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Search className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Quick Select</CardTitle>
                    <CardDescription className="text-zinc-400">
                      Choose from popular laser models
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-500">
                  Pre-configured settings for Glowforge, OMTech, xTool, Ortur, and more
                </p>
              </CardContent>
            </Card>

            <Card 
              className="bg-black/70 backdrop-blur-sm border-zinc-700/50 hover:border-orange-500 transition-all cursor-pointer"
              onClick={() => { setMode('custom'); setStep(1); }}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Settings className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Custom Setup</CardTitle>
                    <CardDescription className="text-zinc-400">
                      Enter your laser specs manually
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-500">
                  For DIY builds or lasers not in our database
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // MODE: PRESET (Quick Selection)
  // ==========================================
  if (mode === 'preset') {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="laser-preset-select"
        style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
      >
        <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => setMode('select')}
            className="mb-4 text-zinc-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <h1 className="text-2xl font-bold text-white mb-6">Select Your Laser</h1>

          {/* Laser Type Toggle */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {[
              { id: 'co2',   label: 'CO2',   color: 'bg-blue-600' },
              { id: 'diode', label: 'Diode', color: 'bg-purple-600' },
              { id: 'fibre', label: 'Fibre', color: 'bg-yellow-600' },
              { id: 'galvo', label: 'Galvo', color: 'bg-green-600' },
            ].map(t => (
              <Button
                key={t.id}
                variant={laserType === t.id ? 'default' : 'outline'}
                className={laserType === t.id ? t.color : 'border-zinc-700'}
                onClick={() => setLaserType(t.id)}
              >
                {t.label} Lasers
              </Button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search lasers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
            />
          </div>

          {/* Presets Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            {filteredPresets.map((preset, idx) => (
              <Card
                key={idx}
                className="bg-black/70 backdrop-blur-sm border-zinc-700/50 hover:border-orange-500 transition-all cursor-pointer"
                onClick={() => selectPreset(preset)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-white">{preset.brand} {preset.model}</div>
                      <div className="text-xs text-zinc-500">
                        {preset.power}W • {preset.bed} bed
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-500" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-sm text-zinc-500 mt-4 text-center">
            Don't see your laser? <button onClick={() => { setMode('custom'); setStep(1); }} className="text-orange-400 hover:underline">Add it manually</button>
          </p>
        </div>
      </div>
    );
  }

  // ==========================================
  // MODE: CUSTOM (Step-by-step wizard)
  // ==========================================
  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="laser-custom-setup"
      style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
    >
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        {step < 5 && (
          <div className="mb-8">
            <div className="flex justify-between text-sm text-zinc-400 mb-2">
              <span>Step {step} of {totalSteps - 1}</span>
              <span>{Math.round((step / (totalSteps - 1)) * 100)}%</span>
            </div>
            <Progress value={(step / (totalSteps - 1)) * 100} className="h-2 bg-zinc-800" />
          </div>
        )}

        {/* Step 1: Laser Type */}
        {step === 1 && (
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader>
              <CardTitle className="text-white">What type of laser do you have?</CardTitle>
              <CardDescription className="text-zinc-400">
                This determines the settings, recommendations, and safety requirements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                {
                  type: 'co2',
                  label: 'CO2 Laser',
                  activeClass: 'border-blue-500 bg-blue-500/20',
                  desc: 'Glass tube lasers (40W–150W+). Cuts wood, acrylic, leather, paper, fabric. Requires water cooling. Most versatile for makers and small businesses.',
                  defaults: { cooling_type: 'water', max_speed: 500, controller: 'ruida' }
                },
                {
                  type: 'diode',
                  label: 'Diode Laser',
                  activeClass: 'border-purple-500 bg-purple-500/20',
                  desc: 'Solid-state lasers (5W–40W). Best for engraving, light cutting, anodised metal marking. Air cooled, compact, affordable. Open-frame — requires enclosure or eye protection.',
                  defaults: { cooling_type: 'air', max_speed: 10000, controller: 'grbl' }
                },
                {
                  type: 'fibre',
                  label: 'Fibre Laser',
                  activeClass: 'border-yellow-500 bg-yellow-500/20',
                  desc: 'Fibre-coupled lasers (20W–100W+). Marks and engraves bare metals permanently. MOPA variants can colour-mark stainless steel. Galvo delivery — extremely fast. Requires full enclosure.',
                  defaults: { cooling_type: 'air', max_speed: 10000, controller: 'galvo' }
                },
                {
                  type: 'galvo',
                  label: 'Galvo Laser',
                  activeClass: 'border-green-500 bg-green-500/20',
                  desc: 'Galvo mirror scanning system (CO2 or fibre source). Ultra-fast marking for serial numbers, barcodes, and production marking. Small field sizes. Requires full enclosure.',
                  defaults: { cooling_type: 'air', max_speed: 10000, controller: 'galvo' }
                },
              ].map(opt => (
                <div
                  key={opt.type}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    laserForm.laser_type === opt.type ? opt.activeClass : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                  onClick={() => setLaserForm({ ...laserForm, laser_type: opt.type, ...opt.defaults })}
                >
                  <div className="font-medium text-white mb-1">{opt.label}</div>
                  <p className="text-sm text-zinc-400">{opt.desc}</p>
                </div>
              ))}

              <Button onClick={() => setStep(2)} className="w-full bg-orange-600 hover:bg-orange-500 mt-2">
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Basic Info */}
        {step === 2 && (
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader>
              <CardTitle className="text-white">Basic Information</CardTitle>
              <CardDescription className="text-zinc-400">
                Tell us about your laser
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Laser Name</Label>
                <Input
                  value={laserForm.name}
                  onChange={(e) => setLaserForm({...laserForm, name: e.target.value})}
                  placeholder="e.g., My K40, Workshop Laser"
                  className="bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Brand</Label>
                  <Input
                    value={laserForm.brand}
                    onChange={(e) => setLaserForm({...laserForm, brand: e.target.value})}
                    placeholder="e.g., OMTech"
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Model</Label>
                  <Input
                    value={laserForm.model}
                    onChange={(e) => setLaserForm({...laserForm, model: e.target.value})}
                    placeholder="e.g., K40"
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Power (Watts)</Label>
                <Select 
                  value={laserForm.power_watts.toString()} 
                  onValueChange={(v) => setLaserForm({...laserForm, power_watts: parseInt(v)})}
                >
                  <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {laserForm.laser_type === 'co2' ? (
                      <>
                        <SelectItem value="40">40W</SelectItem>
                        <SelectItem value="50">50W</SelectItem>
                        <SelectItem value="60">60W</SelectItem>
                        <SelectItem value="80">80W</SelectItem>
                        <SelectItem value="100">100W</SelectItem>
                        <SelectItem value="130">130W</SelectItem>
                        <SelectItem value="150">150W</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="5">5W</SelectItem>
                        <SelectItem value="10">10W</SelectItem>
                        <SelectItem value="20">20W</SelectItem>
                        <SelectItem value="30">30W</SelectItem>
                        <SelectItem value="40">40W</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="border-zinc-700">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setStep(3)} className="bg-orange-600 hover:bg-orange-500">
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Bed & Features */}
        {step === 3 && (
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader>
              <CardTitle className="text-white">Bed Size & Features</CardTitle>
              <CardDescription className="text-zinc-400">
                Configure your work area and capabilities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Bed Width (mm)</Label>
                  <Input
                    type="number"
                    value={laserForm.bed_width}
                    onChange={(e) => setLaserForm({...laserForm, bed_width: parseInt(e.target.value) || 0})}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Bed Height (mm)</Label>
                  <Input
                    type="number"
                    value={laserForm.bed_height}
                    onChange={(e) => setLaserForm({...laserForm, bed_height: parseInt(e.target.value) || 0})}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Controller Type</Label>
                <Select 
                  value={laserForm.controller} 
                  onValueChange={(v) => setLaserForm({...laserForm, controller: v})}
                >
                  <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="ruida">Ruida</SelectItem>
                    <SelectItem value="trocen">Trocen/AWC</SelectItem>
                    <SelectItem value="grbl">GRBL</SelectItem>
                    <SelectItem value="smoothie">Smoothieware</SelectItem>
                    <SelectItem value="proprietary">Proprietary (Glowforge, etc.)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 pt-4 border-t border-zinc-700">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-zinc-300">Air Assist</Label>
                    <p className="text-xs text-zinc-500">Compressed air for cutting</p>
                  </div>
                  <Switch
                    checked={laserForm.has_air_assist}
                    onCheckedChange={(v) => setLaserForm({...laserForm, has_air_assist: v})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-zinc-300">Autofocus</Label>
                    <p className="text-xs text-zinc-500">Automatic focus adjustment</p>
                  </div>
                  <Switch
                    checked={laserForm.has_autofocus}
                    onCheckedChange={(v) => setLaserForm({...laserForm, has_autofocus: v})}
                  />
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)} className="border-zinc-700">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setStep(4)} className="bg-orange-600 hover:bg-orange-500">
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Review & Save */}
        {step === 4 && (
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader>
              <CardTitle className="text-white">Review Your Laser</CardTitle>
              <CardDescription className="text-zinc-400">
                Confirm your settings before saving
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Name</span>
                  <span className="text-white font-medium">{laserForm.name || 'Unnamed Laser'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Type</span>
                  <Badge className={laserForm.laser_type === 'co2' ? 'bg-blue-600' : 'bg-purple-600'}>
                    {laserForm.laser_type.toUpperCase()}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Power</span>
                  <span className="text-white">{laserForm.power_watts}W</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Bed Size</span>
                  <span className="text-white">{laserForm.bed_width} x {laserForm.bed_height} mm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Controller</span>
                  <span className="text-white capitalize">{laserForm.controller}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Air Assist</span>
                  <span className="text-white">{laserForm.has_air_assist ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Autofocus</span>
                  <span className="text-white">{laserForm.has_autofocus ? 'Yes' : 'No'}</span>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(3)} className="border-zinc-700">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={saveLaser} className="bg-green-600 hover:bg-green-500">
                  <Check className="w-4 h-4 mr-2" />
                  Save Laser
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 text-center">
            <CardContent className="py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
                <PartyPopper className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Laser Configured!</h2>
              <p className="text-zinc-400 mb-6">
                Your {laserForm.laser_type.toUpperCase()} laser is ready to use
              </p>

              <div className="flex flex-col gap-3 max-w-xs mx-auto">
                <Button onClick={() => navigate('/laser/calculator')} className="bg-orange-600 hover:bg-orange-500">
                  <Rocket className="w-4 h-4 mr-2" />
                  Try the Calculator
                </Button>
                <Button variant="outline" onClick={() => navigate('/laser')} className="border-zinc-700">
                  Go to Dashboard
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => { setMode('select'); setStep(1); setEditingLaser(null); }}
                  className="text-zinc-400"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Laser
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LaserSetupWizard;
